import { Check, Sparkles, Layout, Briefcase, Code, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export const PricingSection = () => {
  const navigate = useNavigate();

  const plans = [
    {
      name: "Growth Website",
      icon: Layout,
      price: "$1,500",
      priceEnd: "– $3,000",
      period: "",
      bestFor: "Service businesses & funded startups",
      description: "A high-converting business website built to attract global clients.",
      features: [
        "Custom UI/UX design (no templates)",
        "Conversion-focused landing structure",
        "CMS integration (Headless / Firebase)",
        "Technical SEO + schema setup",
        "Speed optimization (90+ Lighthouse)",
        "Analytics & tracking integration",
        "2 years technical support",
      ],
      highlighted: false,
    },
    {
      name: "Scalable Web Platform",
      icon: Briefcase,
      price: "$5,000",
      priceEnd: "– $12,000",
      period: "",
      bestFor: "Serious startups & digital businesses",
      description: "Custom-built web platforms engineered for scale.",
      features: [
        "Custom frontend (React / Next.js)",
        "Secure backend (Node.js / APIs)",
        "Authentication & role-based access",
        "Admin dashboard",
        "Cloud deployment (AWS / GCP)",
        "Database architecture design",
        "Performance & security optimization",
        "2 years technical support",
      ],
      highlighted: true,
    },
    {
      name: "SaaS Product Development",
      icon: Code,
      price: "$15,000",
      priceEnd: "– $40,000+",
      period: "",
      bestFor: "Founders building funded or revenue-backed SaaS",
      description: "End-to-end SaaS architecture, built for long-term scalability.",
      features: [
        "Complete product architecture planning",
        "Frontend + Backend development",
        "Payment integrations (Stripe)",
        "Subscription & billing system",
        "Multi-tenant architecture",
        "DevOps & CI/CD setup",
        "Security best practices",
        "Dedicated development team",
        "2 years priority technical support",
      ],
      highlighted: false,
    },
    {
      name: "Dedicated Development Team",
      icon: CalendarClock,
      price: "$2,500",
      priceEnd: "– $8,000",
      period: "/month",
      bestFor: "Long-term product scaling",
      description: "Your remote tech team — without hiring overhead.",
      features: [
        "Full-time dedicated developer(s)",
        "Weekly sprint planning",
        "Architecture guidance",
        "Ongoing feature development",
        "Performance monitoring",
        "SEO & technical optimization",
        "Priority support & maintenance",
      ],
      highlighted: false,
    },
  ];

  return (
    <section id="pricing" className="py-16 sm:py-20 lg:py-24 relative px-4 sm:px-6 lg:px-8 xl:px-12">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />

      <div className="container mx-auto relative z-10 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-primary/20 mb-4 sm:mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Recommended Pricing (USD)</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-4 sm:mb-6">
            Choose your
            <span className="gradient-text"> perfect plan</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto px-2">
            Transparent pricing. No hidden fees. Scale as you grow.
          </p>
        </div>

        {/* All 4 Plans - equal height, responsive grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 items-stretch">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative flex flex-col rounded-2xl p-5 sm:p-6 lg:p-8 min-h-0 ${
                plan.highlighted
                  ? "glass-card border-2 border-primary/50"
                  : "glass-card"
              } hover-lift`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 px-3 sm:px-4 py-1 rounded-full bg-gradient-to-r from-primary to-accent text-primary-foreground text-xs sm:text-sm font-medium whitespace-nowrap">
                  Most Popular
                </div>
              )}
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex-shrink-0 ${plan.highlighted ? "bg-gradient-to-br from-primary to-accent" : "bg-primary/10"} flex items-center justify-center mb-4 sm:mb-6`}>
                <plan.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${plan.highlighted ? "text-primary-foreground" : "text-primary"}`} />
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-semibold mb-1">{plan.name}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4 line-clamp-2">Best for: {plan.bestFor}</p>
              <div className="mb-3 sm:mb-4 flex flex-wrap items-baseline gap-1">
                <span className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold">{plan.price}</span>
                <span className="text-lg sm:text-xl lg:text-2xl font-heading font-bold text-muted-foreground">{plan.priceEnd}</span>
                <span className="text-muted-foreground text-sm sm:text-base">{plan.period}</span>
              </div>
              <p className="text-muted-foreground text-xs sm:text-sm mb-4 sm:mb-6 line-clamp-2">{plan.description}</p>
              <ul className="space-y-2 sm:space-y-3 flex-1 min-h-0">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 sm:gap-3">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-primary" />
                    </div>
                    <span className="text-xs sm:text-sm text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-10 lg:mt-12">
          <Button size="lg" className="glow-primary bg-gradient-to-r from-primary to-accent" onClick={() => navigate("/contact#contact-form")}>
            Get a custom quote
          </Button>
        </div>
      </div>
    </section>
  );
};
